import Button from '@components/DesignLibrary/Button';
import React, { useCallback } from 'react';
import getAiAgents from '@tsClient/questionnaires/getAiAgents';
import Bluebird from 'bluebird';
import getListOrTableField from '@helpers/getListOrTableField';
import parseFieldKey from '@/components/Questionnaire/form/helpers/parseFieldKey';
import { get, uniq } from 'lodash';
import Chunks from '@components/Questionnaire/form/HandleWatchers/HandleAiAgentAutofills/helpers/Chunks';
import removeNullish from '@helpers/removeNullish';
import { useQuestionnaireContext, useQuestionnaireFormContext } from '@components/Questionnaire/QuestionnaireProvider';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { Questionnaire } from '@typings/Questionnaire';
import evaluateExpression from '@helpers/evaluateExpression';
import FieldKeyConstants from '../helpers/fieldKeys';

type Props = {
  agents: Awaited<ReturnType<typeof getAiAgents>>['items'];
  field: Pick<Questionnaire.Field, 'id' | 'key'>;
};

const RunListAgentButton: React.FC<Props> = ({ field: fullListField, agents }) => {
  const [isRunningListAgent, setIsRunningListAgent] = React.useState(false);

  const {
    formMethods,
    setValue,
    getResponseSource,
  } = useQuestionnaireFormContext();

  const {
    fieldsByKey,
    fieldsById,
    questionnaireId,
    userWorkflowId,
  } = useQuestionnaireContext();

  const { getData: runAiAgentFn } = useNoodleApi(tsClient.questionnaires.runAiAgent);

  const runListAgent = useCallback(async () => {
    setIsRunningListAgent(true);
    await Bluebird.mapSeries(agents, async (aiAgent) => {
      const listFieldValues = formMethods.getValues()[fullListField.key] as Array<unknown>;
      const validIndices = listFieldValues.reduce((acc: Array<number>, val, index) => {
        if (!aiAgent.listFilterExpression?.expression) {
          return [...acc, index];
        }
        const variables = aiAgent.listFilterExpression.variables.filter((v) => v.referenceType === 'field-template-key')
          .reduce((variableAcc, variableVal) => ({
            ...variableAcc,
            [variableVal.variableName]: (val as Record<string, unknown>)?.[variableVal.referenceId]
            || formMethods.getValues()[variableVal.referenceId],
          }), {});
        const isValid = Boolean(evaluateExpression({
          expression: aiAgent.listFilterExpression.expression,
          variables,
        }));
        if (isValid) {
          return [...acc, index];
        }
        return acc;
      }, []);
      const inputFieldKeys = aiAgent.fieldData.flatMap((fieldKey) => {
        const field = fieldsByKey[fieldKey];
        if (field?.listFieldTemplateId) {
          const listField = fieldsById[field.listFieldTemplateId];
          if (listField) {
            if (!listFieldValues || !Array.isArray(listFieldValues) || listFieldValues?.length === 0) {
              return [];
            }
            return listFieldValues.map((__, index) => validIndices.includes(index) ? `${listField.key}[${index}].${fieldKey}` : null).filter(removeNullish);
          }
        }
        return fieldKey;
      });
      const outputFieldKeys = aiAgent.fieldOutputs.flatMap((fieldKey) => {
        const field = fieldsByKey[fieldKey];
        if (!field) {
          return [];
        }
        const listField = getListOrTableField({
          fieldId: field?.listFieldTemplateId,
          fieldsById,
        });

        if (listField) {
          if (!listFieldValues || !Array.isArray(listFieldValues) || listFieldValues?.length === 0) {
            return [];
          }
          return listFieldValues.map((__, index) => validIndices.includes(index) ? `${listField.key}[${index}].${fieldKey}` : null).filter(removeNullish);
        }
        return fieldKey;
      });
      const listInputFieldKeys = listFieldValues.map((_, index) => aiAgent.listInputs.map(fieldKey => `${fullListField.key}[${index}].${fieldKey}`))
        .reduce((acc, val) => [...acc, ...val], []);
      let listInputData: Record<string, Record<string, string | number>[]> = {};
      const keysByIndex = listInputFieldKeys.reduce((acc: Record<string, string[]>, fieldKey) => {
        const parsedFieldKey = parseFieldKey(fieldKey);
        if (parsedFieldKey.rowIndex !== null && validIndices.includes(parsedFieldKey.rowIndex)) {
          if (acc[parsedFieldKey.rowIndex]) {
            acc[parsedFieldKey.rowIndex].push(fieldKey);
          } else {
            acc[parsedFieldKey.rowIndex] = [fieldKey];
          }
        }
        return acc;
      }, {});
      if (aiAgent.groupByFieldKey) {
        validIndices.forEach((listFieldIndex) => {
          if (!aiAgent.groupByFieldKey) {
            return;
          }
          const keys = keysByIndex[listFieldIndex];
          let group = get(formMethods.getValues(), `${fullListField.key}[${listFieldIndex}].${aiAgent.groupByFieldKey}`) || 'Other';
          const options = fieldsByKey[aiAgent.groupByFieldKey]?.options || [];
          group = options.find((o) => o.key === group)?.label ?? group;
          const listInputItem = {
            index: listFieldIndex,
            ...keys.reduce((acc, val) => {
              const parsedFieldKey = parseFieldKey(val);
              const field = fieldsByKey[parsedFieldKey.fieldKey];
              if (field) {
                return {
                  ...acc,
                  [fieldsByKey[parsedFieldKey.fieldKey].label]: get(formMethods.getValues(), parsedFieldKey.fullFieldKey),
                };
              }
              return acc;
            }, {}),
          };
          if (listInputData[group]) {
            listInputData[group].push(listInputItem);
          } else {
            listInputData[group] = [listInputItem];
          }
        });
      } else {
        listInputData = listInputFieldKeys.reduce((acc, val) => {
          const parsedFieldKey = parseFieldKey(val);
          if (parsedFieldKey.rowIndex !== null && !validIndices.includes(parsedFieldKey.rowIndex)) {
            return acc;
          }
          return {
            ...acc,
            [val]: {
              label: `${fieldsByKey[parsedFieldKey.fieldKey || parsedFieldKey.fullFieldKey]?.label} (index ${parsedFieldKey.rowIndex})`,
              value: get(formMethods.getValues(), parsedFieldKey.fullFieldKey),
            },
          };
        }, {});
      }
      const chunkHandler = new Chunks({
        aiAgent,
        fieldsByKey,
        formValues: formMethods.getValues(),
        getResponseSource,
        inputFieldKeys,
        outputFieldKeys,
        size: 5,
      });

      chunkHandler.addOutputFields({ unset: false });

      await Bluebird.mapSeries(chunkHandler.chunks, async (chunk) => {
        const { data: result } = await runAiAgentFn({
          agentId: aiAgent.id,
          creatorId: aiAgent.creatorId,
          fieldInputData:{
            ...chunk.fieldInputData,
            ...listInputData,
          },
          questionnaireId,
          questionnaireTemplateId: aiAgent.questionnaireTemplateId,
          rowIndiciesToRun: uniq(chunk.fieldOutputs.map((of) => of.rowIndex).filter(removeNullish)),
          stateOfFiling: formMethods.getValues()[FieldKeyConstants.STATE_OF_FILING],
          userWorkflowId,
        });
        if (result) {
          Object.entries(result).forEach(([key, value]) => {
            if (key !== 'aiRequestId' && !key.includes('__ExplanationHTML')) {
              const splitKey = key.split('].');
              let keyBuilt = key;
              if(splitKey.length > 1) {
                [,keyBuilt] = splitKey;
              }
              const explanationHtml = result[`${key}__ExplanationHTML`] ?? null;
              setValue({ aiRequestId: result?.aiRequestId, explanationHtml, key, source: 'ai', value});
              if(key === keyBuilt) {
                const fieldsFetched = fieldsByKey[keyBuilt];
                fieldsFetched?.linkedFieldKeys?.forEach((linkedFieldKey) => {
                  setValue({ aiRequestId: result?.aiRequestId, explanationHtml, key:linkedFieldKey, source: 'ai', value});
                });
              }
            }
          });
        }
      });
    });
    setIsRunningListAgent(false);
  }, [fieldsById, fieldsByKey, formMethods, getResponseSource, agents, fullListField, questionnaireId, runAiAgentFn, userWorkflowId, setValue]);

  return (
    <Button variant='ai' size='sm' onClick={runListAgent} loading={isRunningListAgent}>Run {agents.find(a => a.title)?.title || 'List'} Agent</Button>
  );
};

export default RunListAgentButton;
